import { useCallback, useEffect, useMemo, useRef } from 'react';
import { logger } from '../logger';

/**
 * Hook that allows communication between different documents (in different windows, tabs, frames or iframes) of the same origin.
 * See: https://developer.mozilla.org/en-US/docs/Web/API/BroadcastChannel
 * @param channelName - channel name - used as unique identifier
 * @param onMessage - callback fired when message is received
 * @param onMessageError - callback fired when message error is received
 */
export function useBroadcastChannel<DATA>(
  channelName: string,
  onMessage?: (event: MessageEvent<DATA>) => void,
  onMessageError?: (data: MessageEvent<DATA>) => void
) {
  const channelRef = useRef<BroadcastChannel>();

  useEffect(() => {
    const bc = new BroadcastChannel(channelName);
    channelRef.current = bc;
    return () => {
      bc.close();
    };
  }, [channelName]);

  useEffect(() => {
    if (onMessage) {
      channelRef.current?.addEventListener('message', onMessage);
    }
    return () => {
      if (onMessage) {
        channelRef.current?.removeEventListener('message', onMessage);
      }
    };
  }, [onMessage]);

  useEffect(() => {
    if (onMessageError) {
      channelRef.current?.addEventListener('messageerror', onMessageError);
    }
    return () => {
      if (onMessageError) {
        channelRef.current?.removeEventListener('messageerror', onMessageError);
      }
    };
  }, [onMessageError]);

  const broadcast = useCallback(
    (data: DATA) => {
      if (!channelRef.current) {
        logger.warn(`Broadcast channel is not initialized for a channel: ${channelName}`);
        return;
      }
      channelRef.current.postMessage(data);
    },
    [channelName]
  );

  return useMemo(
    () => ({
      channel: channelRef.current,
      broadcast
    }),
    [broadcast]
  );
}
